/**
 * jolly-gut-supply.js
 *
 * Two-step weight × supply selector for JOLLY GUT (single-SKU model).
 *
 * The customer always buys the 200 g variant; the chosen weight × duration
 * tile sets the quantity on the buy-button form. Shopify automatic discounts
 * (qty ≥ N, one per tier) deliver the per-bottle price at checkout.
 *
 * --- SOURCE-OF-TRUTH NOTE ---
 * PRICE_BY_QTY below must mirror the 6 Shopify automatic discounts
 * (titles: "Jolly Gut · Buy N bottles · X% off"). If those drift, update
 * both. The Jest test theme-jollygut-noruff-selector.test.js asserts the
 * code-side values; live drift is caught by manual QA only.
 *
 * Dose chart (on-pack, administered twice daily, 1 scoop ≈ 2 g, 200 g bottle):
 *   ≤10 kg  → 1 scoop/day → 1 bottle lasts 100 days
 *   10–20 kg → 2 scoops/day → 50 days
 *   20–40 kg → 3 scoops/day → 33 days (chart rounds to 30)
 *   >40 kg  → 5 scoops/day → 20 days
 */

(function () {
  // A missing engine would otherwise throw a bare TypeError.
  if (!window.UwPackPlan) {
    console.error(
      "[pack-plan] uw-pack-plan.js must load before jolly-gut-supply.js",
    );
    return;
  }

  const SCOOPS_PER_DAY_BY_WEIGHT = {
    small: 1,
    medium: 2,
    large: 3,
    xlarge: 5,
  };

  // (weight, tile index) → bottles shipped + marketing duration label.
  // Bottles chosen so the customer never runs out at chart dose. The xlarge
  // Most Savings tile is capped at "140 day pack" (qty=7) to stay inside the
  // approved ₹700/bottle floor — a true 180-day pack for a giant breed would
  // need 9 bottles, beyond the discount ladder.
  const SUPPLY_BY_WEIGHT_AND_TILE = {
    small: [
      { qty: 1, days: 60 },
      { qty: 2, days: 120 },
      { qty: 3, days: 240 },
    ],
    medium: [
      { qty: 1, days: 45 },
      { qty: 2, days: 90 },
      { qty: 4, days: 180 },
    ],
    large: [
      { qty: 2, days: 45 },
      { qty: 3, days: 90 },
      { qty: 6, days: 180 },
    ],
    xlarge: [
      { qty: 3, days: 45 },
      { qty: 5, days: 90 },
      { qty: 7, days: 140 },
    ],
  };

  // Discounted total price per bottle count (matches the 6 Shopify auto-discounts).
  const PRICE_BY_QTY = {
    1: 899,
    2: 1699,
    3: 2449,
    4: 3099,
    5: 3749,
    6: 4299,
    7: 4949,
  };

  const BASE_PRICE_PER_BOTTLE = 899;
  const MRP_PER_BOTTLE = 999;
  // "Jolly Gut · Buy 7 bottles · 21.4% off" is the best qualifying discount
  // for 8+ bottles too (several dogs on one order).
  const TOP_TIER_DISCOUNT = 0.214;

  // Store-wide shipping rule: free over ₹1,000 (mirrored in the PDP trust bar,
  // sections/uw-pdp-nav.liquid).
  const FREE_SHIPPING_THRESHOLD = 1000;

  window.UwPackPlan.define("jolly-gut-two-step", {
    key: "jolly-gut",
    unit: "200 g",
    noun: "pet",
    propertyName: "Pet",
    SUPPLY_BY_WEIGHT_AND_TILE,
    PRICE_BY_QTY,
    BASE_PRICE_PER_BOTTLE,
    MRP_PER_BOTTLE,
    TOP_TIER_DISCOUNT,
    FREE_SHIPPING_THRESHOLD,
    dose: (weight) => {
      const scoops = SCOOPS_PER_DAY_BY_WEIGHT[weight];
      return scoops === 1 ? "1 scoop" : `${scoops} scoops`;
    },
  });
})();
